import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react'
import { User } from 'firebase/auth'
import { auth } from '../firebase'

interface AuthContextType {
  user: User | null
  authToken: string | null
  isLoading: boolean
  userID: string | null
  isAuthed: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setLoading] = useState<boolean>(true)
  const [authToken, setAuthToken] = useState<string | null>(null)

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser)
      setLoading(false)
    })

    const unsubscribeAuth = auth.onIdTokenChanged(async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken()
          setAuthToken(token)
        } catch (error) {
          console.error('Failed to get auth token:', error)
        }
      } else {
        setAuthToken(null)
      }
    })

    return () => {
      unsubscribe()
      unsubscribeAuth()
    }
  }, [])

  const authState = {
    user,
    authToken,
    isLoading,
    userID: user?.uid || null,
    isAuthed: !!user,
  }

  return (
    <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>
  )
}

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
